import React, { Dispatch, SetStateAction, useEffect, useState } from "react"
import { View, TextInput, Pressable, ScrollView } from "react-native"
import { ZenButton, ZenText, ZenIcon, ZenDivider } from "zen-core-elements"
import { ThemeConsumer } from "@rneui/themed"
import { appColors, appFonts, fontSize } from "../../themes/appTheme"
import { useAppSelector } from "../../redux/hooks"
import CommonService from "../../shared/services/commonService"
import { isValidAmount } from "../../redux/utils/discountUtils"

const CustomInput = ({
    label,
    value,
    onChangeText,
    placeholder,
    iconName,
    iconType,
    keyboardType = 'default',
    errorMessage,
    onBlur
}: {
    label?: string;
    value: string;
    onChangeText: (text: string) => void;
    placeholder: string;
    iconName: string;
    iconType: string;
    keyboardType?: 'default' | 'numeric' | 'email-address';
    errorMessage?: string;
    onBlur?: () => void;
}) => {
    return (
        <View style={{ marginBottom: 16 }}>
            {label ? (
                <ZenText content={label} size={fontSize.smallFontSize} color="#475569" style={{ marginBottom: 8, fontFamily: appFonts.bold }} />
            ) : null}
            <View style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: '#F1F5F9', // Soft slate
                borderRadius: 12,
                paddingHorizontal: 14,
                height: 48,
                borderWidth: errorMessage ? 1 : 0,
                borderColor: errorMessage ? appColors.red : 'transparent',
            }}>
                <ZenIcon name={iconName} type={iconType} size={20} color="#64748B" style={{ marginRight: 8 }} />
                <TextInput
                    value={value}
                    onChangeText={onChangeText}
                    placeholder={placeholder}
                    placeholderTextColor="#94A3B8"
                    keyboardType={keyboardType}
                    onBlur={onBlur}
                    style={{
                        flex: 1,
                        fontSize: fontSize.mediumFontSize,
                        color: '#0F172A',
                        padding: 0,
                        fontFamily: appFonts.medium,
                    }}
                />
            </View>
            {errorMessage ? (
                <ZenText content={errorMessage} color={appColors.red} size={fontSize.errorFontSize} style={{ marginTop: 4, marginLeft: 4 }} />
            ) : null}
        </View>
    );
};

const FormTemplate = ({
    event,
    value,
    setValue,
    reasonValue,
    setReasonValue,
    orderData,
    handleUpdateRefundAmount,
    isValueEnable,
    onClose
}: {
    event: 'Refund' | 'Loyalty';
    value: string;
    setValue: Dispatch<SetStateAction<string>>;
    reasonValue: string;
    setReasonValue: Dispatch<SetStateAction<string>>;
    orderData: {
        totalRefundAmount: number;
        availablePointsToRedeem: number;
        totalAmount: string;
        earningPoints: string
    };
    handleUpdateRefundAmount: (event: "Refund" | "Loyalty") => Promise<void>;
    isValueEnable: boolean;
    onClose: () => void;
}) => {

    const [selectedMode, setSelectedMode] = useState<'Full' | 'Partial'>(
        isValueEnable ? 'Full' : 'Partial'
    )

    const [handleTouch, setHandleTouch] = useState({ 'form1': false, 'form2': false })
    const [valueError, setValueError] = useState('')
    const [reasonError, setReasonError] = useState('')

    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
    const points = Number(orderData?.availablePointsToRedeem ?? orderData?.earningPoints ?? 0)

    const remainingRefundAmount = orderData?.totalRefundAmount
        ? Number(orderData?.totalAmount) - orderData?.totalRefundAmount
        : Number(orderData?.totalAmount)

    useEffect(() => {
        if (selectedMode === 'Full') {
            setValue(event === 'Loyalty' ? String(points) : String(remainingRefundAmount))
        } else {
            setValue(value)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMode])

    const formErrorHelper = (ischanged: boolean, rawValue: string, eventType: 'Refund' | 'Loyalty') => {
        let errMsg = ''
        const validValue = Number(rawValue)
        // Number() alone accepted "-500", "0x10" and "1e2" (and decimal points)
        const badFormat = eventType === 'Refund' ? !isValidAmount(rawValue) : !/^\d+$/.test(rawValue)
        if (ischanged && rawValue && badFormat) {
            errMsg = eventType === 'Refund' ? 'Enter a valid refund amount' : 'Points must be a whole number'
        }
        else if (ischanged && validValue && orderData) {
            if (eventType === 'Refund' && !(validValue <= remainingRefundAmount)) {
                errMsg = 'refund amount should be in limit'
            }
            else if (eventType === 'Loyalty' && !(validValue <= points)) {
                errMsg = 'loyalty amount should be in limit'
            }
        }
        else if (ischanged && !validValue) {
            errMsg = `${eventType === 'Refund' ? 'Refund Amount' : 'Adjust points'} is required`
        }
        setValueError(errMsg)
        return errMsg
    }

    const reasonFormhelper = (isChanged: boolean, validValue: string) => {
        let errMsg = ''
        if (isChanged && validValue) {
            if (!validValue.match(/^[a-zA-Z\s]+$/)) {
                errMsg = 'Reason accepts characters and spaces only'
            }
        }
        else if (isChanged) {
            errMsg = 'Reason is required'
        }
        setReasonError(errMsg)
        return errMsg
    }

    const handleSelectMode = (mode: 'Full' | 'Partial') => {
        setSelectedMode(mode)
        setHandleTouch((prev) => ({ ...prev, form1: false }))
        setValueError('')
        if (mode === 'Full') {
            setValue(event === 'Loyalty' ? String(points) : String(remainingRefundAmount))
        } else {
            setValue('')
        }
    }

    const renderCard = (mode: 'Full' | 'Partial') => {
        const isSelected = selectedMode === mode
        let title = ''
        let subtitle = ''
        let iconName = ''
        let iconType = 'material-community'

        if (event === 'Refund') {
            if (mode === 'Full') {
                title = 'Full Refund'
                subtitle = `Refund remaining: ${CommonService.currencyStandard(currencySymbol, String(remainingRefundAmount ?? 0))}`
                iconName = 'cash-multiple'
            } else {
                title = 'Partial Refund'
                subtitle = 'Refund a specific custom amount'
                iconName = 'cash-minus'
            }
        } else {
            if (mode === 'Full') {
                title = 'Full Adjustment'
                subtitle = `Adjust all points: ${points}`
                iconName = 'star'
            } else {
                title = 'Partial Adjustment'
                subtitle = 'Adjust custom number of points'
                iconName = 'star-outline'
            }
        }

        return (
            <Pressable
                onPress={() => handleSelectMode(mode)}
                style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: 16,
                    backgroundColor: isSelected ? '#EFF6FF' : '#ffffff', // Selected light blue
                    borderRadius: 16,
                    borderWidth: 2,
                    borderColor: isSelected ? '#2563EB' : '#E2E8F0', // Selected blue, unselected grey
                    marginBottom: 12,
                    shadowColor: "#000",
                    shadowOffset: { width: 0, height: 1 },
                    shadowOpacity: 0.05,
                    shadowRadius: 3,
                    elevation: 2,
                }}
            >
                {/* Left Icon inside circle */}
                <View style={{
                    width: 44,
                    height: 44,
                    borderRadius: 22,
                    backgroundColor: isSelected ? '#DBF5FF' : '#F1F5F9',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginRight: 16
                }}>
                    <ZenIcon
                        name={iconName}
                        type={iconType}
                        size={20}
                        color={isSelected ? '#208fb8' : '#8f8f8f'}
                    />
                </View>

                {/* Middle Text Details */}
                <View style={{ flex: 1 }}>
                    <ZenText
                        content={title}
                        bold
                        size={fontSize.mediumFontSize}
                        color={isSelected ? '#0F172A' : '#414141'}
                    />
                    <ZenText
                        content={subtitle}
                        size={fontSize.smallFontSize}
                        color={isSelected ? '#475569' : '#8f8f8f'}
                        style={{ marginTop: 2 }}
                    />
                </View>

                {/* Right Checkbox / Radio indicator */}
                <View style={{
                    width: 24,
                    height: 24,
                    borderRadius: 12,
                    borderWidth: isSelected ? 0 : 2,
                    borderColor: '#E2E8F0',
                    backgroundColor: isSelected ? '#10B981' : 'transparent', // Selected green checkmark
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    {isSelected && (
                        <ZenIcon
                            name="check"
                            type="feather"
                            size={14}
                            color="#ffffff"
                        />
                    )}
                </View>
            </Pressable>
        )
    }

    return (
        <ThemeConsumer>
            {({ theme }: any) => (
                <View style={{ backgroundColor: '#ffffff', width: '100%' }}>
                    {/* Header */}
                    <View style={{
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        paddingHorizontal: 24,
                        paddingVertical: 18,
                        borderBottomWidth: 1,
                        borderBottomColor: '#F1F5F9',
                    }}>
                        <View>
                            <ZenText
                                content={event === 'Refund' ? 'Cash Refund' : 'Adjust Loyalty Points'}
                                bold
                                size={fontSize.headerFontSize}
                                color={appColors.primary}
                            />
                            <ZenText
                                content={event === 'Refund' ? 'Process refund amount for this sale' : 'Modify the customer\'s loyalty points balance'}
                                size={fontSize.smallFontSize}
                                color="#64748B"
                                style={{ marginTop: 2 }}
                            />
                        </View>
                        <Pressable
                            onPress={onClose}
                            style={{
                                width: 32,
                                height: 32,
                                borderRadius: 16,
                                backgroundColor: '#F1F5F9',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <ZenIcon name='close' type='material' size={18} color="#64748B" />
                        </Pressable>
                    </View>

                    {/* Content Scroll View */}
                    <ScrollView contentContainerStyle={{ paddingHorizontal: 24, paddingVertical: 20 }}>
                        {/* Options Cards */}
                        {renderCard('Full')}
                        {renderCard('Partial')}

                        <ZenDivider style={{ marginVertical: 16, borderColor: '#F1F5F9', borderWidth: 0.5 }} />

                        {/* Input Fields */}
                        {selectedMode === 'Partial' && (
                            <CustomInput
                                label={event === 'Refund' ? 'Refund Amount' : 'Adjust points'}
                                value={value}
                                placeholder={event === 'Refund' ? 'Enter refund amount' : 'Enter points to adjust'}
                                iconName={event === 'Refund' ? 'cash' : 'star'}
                                iconType="material-community"
                                keyboardType="numeric"
                                onBlur={() => {
                                    setHandleTouch((prev) => ({ ...prev, form1: true }))
                                    formErrorHelper(true, String(value ?? ''), event)
                                }}
                                onChangeText={(val) => {
                                    setValue(val)
                                    formErrorHelper(handleTouch.form1, val, event)
                                }}
                                errorMessage={valueError}
                            />
                        )}

                        <CustomInput
                            label={`Reason for ${event === 'Refund' ? 'Refund' : 'Adjustment'}`}
                            value={reasonValue}
                            placeholder={`Enter reason for ${event.toLowerCase()}`}
                            iconName="note-outline"
                            iconType="material-community"
                            onBlur={() => {
                                setHandleTouch((prev) => ({ ...prev, form2: true }))
                                reasonFormhelper(true, reasonValue ?? '')
                            }}
                            onChangeText={(val) => {
                                setReasonValue(val)
                                reasonFormhelper(handleTouch.form2, val)
                            }}
                            errorMessage={reasonError}
                        />

                        {/* Modern Premium Note Section */}
                        <View style={{
                            padding: 14,
                            backgroundColor: '#EFF6FF', // Light blue matching orderList badge background
                            borderRadius: 12,
                            borderWidth: 1,
                            borderColor: '#DBF5FF',
                            flexDirection: 'row',
                            alignItems: 'flex-start',
                            marginBottom: 16,
                        }}>
                            <ZenIcon
                                name="information-outline"
                                type="material-community"
                                size={18}
                                color="#1E40AF"
                                style={{ marginRight: 8, marginTop: 1 }}
                            />
                            <ZenText
                                style={{ flex: 1, lineHeight: 18 }}
                                size={fontSize.badgeFontSize + 1}
                                color="#1E40AF"
                                bold
                                content={event === 'Refund'
                                    ? `Note: Refund amount should be within the paid amount: ${CommonService.currencyStandard(currencySymbol, String(remainingRefundAmount ?? 0))}`
                                    : `Note: Adjust points should be within the available loyalty points: ${points}`
                                }
                            />
                        </View>

                        {/* Action Button */}
                        <ZenButton
                            title={event === 'Refund' ? 'Confirm Refund' : 'Confirm Adjustment'}
                            disabled={
                                selectedMode === 'Partial'
                                    ? !value || !!valueError || !reasonValue || !!reasonError
                                    : !reasonValue || !!reasonError
                            }
                            containerStyle={{ width: '100%', height: 48, marginTop: 8 }}
                            titleStyle={{
                                fontFamily: appFonts.bold,
                                fontSize: fontSize.mediumFontSize,
                                color: appColors.light,
                            }}
                            buttonStyle={{
                                backgroundColor: appColors.primary,
                                borderRadius: 12,
                                height: 48,
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                            onPress={() => {
                                handleUpdateRefundAmount(event)
                            }}
                        />
                    </ScrollView>
                </View>
            )}
        </ThemeConsumer>
    )
}

export default FormTemplate
